import {MyTableConfigurationsDocument} from 'authentication/hooks/myTableConfigurations.generated';
import React, {useCallback, useState} from 'react';
import {Column} from 'types';
import {HideColumnsPanel} from './HideColumnsPanel';
import {ColumnTripleEditRegular} from '@fluentui/react-icons';
import {EntityListColumn} from './EntityList';
import {useCommand, useNotificationBar} from 'hooks';
import {useTranslation} from 'react-i18next';
import {useSetTableConfigurationMutation} from './hooks/useSetTableConfigurationMutation.generated';
import {ContextualMenuItemType} from 'components/FluentUICompat';

type EntityListColumnConfigProps = {
	columns: EntityListColumn[];
	tableId?: string;
	enhanceColumnVisibilityButton?: boolean;
	currentColumnConfig: Column[];
};

export const EntityListColumnConfigPanel: React.FC<
	EntityListColumnConfigProps
> = props => {
	const {t} = useTranslation('components/entitylist');

	const [hideColumnsPanelOpen, setHideColumnsPanelOpen] = useState(false);

	const saveColumnConfiguration = useCallback(
		async (config: Column[]) => {
			if (props.tableId) {
				await setTableConfiguration({
					variables: {
						input: {
							columns: config,
							tableId: props.tableId,
						},
					},
					refetchQueries: [MyTableConfigurationsDocument],
				}).then(() => {
					onHideColumnsPanelDismiss();
					setMessage(t('UpdatedTableConfiguration'));
				});
			}
		},
		[props.tableId],
	);

	const onColumnConfigSaveClick = useCallback(() => {
		saveColumnConfiguration(props.currentColumnConfig);
	}, [saveColumnConfiguration, props.currentColumnConfig]);

	const resetColumnConfiguration = useCallback(async () => {
		if (props.tableId) {
			await setTableConfiguration({
				variables: {
					input: {
						columns: props.columns.map(c => ({
							hidden: Boolean(c.hidden),
							key: c.key,
							width: c.minWidth,
						})),
						tableId: props.tableId,
					},
				},
				refetchQueries: [MyTableConfigurationsDocument],
			}).then(() => {
				setMessage(t('ResetTableConfiguration'));
			});
		}
	}, [props.columns]);

	const onColumnConfigResetClick = useCallback(async () => {
		await resetColumnConfiguration();
	}, [resetColumnConfiguration]);

	const {setMessage} = useNotificationBar();

	const [setTableConfiguration] = useSetTableConfigurationMutation();

	const onHideColumnsPanelOpen = useCallback(() => {
		setHideColumnsPanelOpen(true);
	}, []);

	const onHideColumnsPanelDismiss = useCallback(() => {
		setHideColumnsPanelOpen(false);
	}, []);

	useCommand(
		{
			key: 'tableConfigurations',
			iconProps: {
				iconName: 'More',
			},
			farCommand: true,
			hidden: !props.tableId,
			subMenuProps: {
				items: [
					...(props.enhanceColumnVisibilityButton === true
						? []
						: [
								{
									key: 'configureHiddenColumns',
									iconProps: {iconName: 'ColumnOptions'},
									onClick: onHideColumnsPanelOpen,
									text: t('OpenHiddenColmnsConfig'),
								},
						  ]),
					{
						key: 'saveTableConfiguration',
						iconProps: {iconName: 'Save'},
						onClick: onColumnConfigSaveClick,
						text: t('SaveTableConfiguration'),
					},
					{
						key: 'divider',
						itemType: ContextualMenuItemType.Divider,
					},
					{
						key: 'resetTableConfiguration',
						iconProps: {
							iconName: 'Cancel',
						},
						onClick: onColumnConfigResetClick,
						text: t('ResetTableConfiguration'),
					},
				],
			},
			priority: 100,
		},
		[props.tableId, onColumnConfigSaveClick, onColumnConfigResetClick],
	);

	useCommand({
		key: 'configureHiddenColumns',
		iconProps: {iconName: 'ColumnOptions'},
		onClick: onHideColumnsPanelOpen,
		text: t('OpenHiddenColmnsConfig'),
		farCommand: true,
		hidden: !props.tableId || props.enhanceColumnVisibilityButton !== true,
		priority: 15,
		onRender(item: any) {
			return (
				<div
					className='prominent-button-container'
					style={{
						display: 'flex',
						height: '100%',
						flexWrap: 'nowrap',
						justifyContent: 'center',
						alignItems: 'center',
						cursor: 'pointer',
						textAlign: 'center',
						maxWidth: '160px',
						fontWeight: 'bolder',
					}}
					onClick={item.onClick}
				>
					<ColumnTripleEditRegular
						className='button-icon'
						aria-label='Info'
						style={{
							color: '#B12525',
							fontSize: '36px',
							paddingLeft: '4px',
							paddingTop: '2px',
						}}
					/>
					{item.text}
				</div>
			);
		},
	});

	return (
		<HideColumnsPanel
			isOpen={hideColumnsPanelOpen}
			columns={props.columns}
			onDismiss={onHideColumnsPanelDismiss}
			onSubmit={saveColumnConfiguration}
		/>
	);
};
